import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import type { StatusType } from '@repo/design-system-kit';
import { BadgeStatus, FormattedDate, FormattedMoney } from '@repo/design-system-kit';
import type { Quote } from 'app/react/api/models/quote';
import styles from './information.module.css';

interface QuoteInformationProps extends ComponentPropsWithoutRef<'div'> {
  quote: Quote;
}

export function QuoteInformation({ quote, className, ...props }: QuoteInformationProps): ReactNode {
  const { formatMessage, locale } = useIntl();

  const getStatus = (): { level: StatusType; text: string } => {
    const { status } = quote;
    switch (status) {
      case 'pending_approval':
        return {
          level: 'in-progress',
          text: formatMessage({ id: 'quote.preview.status.pending' }),
        };
      case 'approved':
        return {
          level: 'validated',
          text: formatMessage({ id: 'quote.preview.status.approved' }),
        };
      case 'canceled':
        return {
          level: 'declined',
          text: formatMessage({ id: 'quote.preview.status.canceled' }),
        };
      default:
        return {
          level: 'inactive' as const,
          text: status,
        };
    }
  };

  const status = getStatus();
  const hasExpired = quote.expiryDate ? new Date(quote.expiryDate) < new Date() : false;

  return (
    <div className={cx(className)} {...props}>
      <BadgeStatus
        className="mb-16"
        data-test-quote-information-status=""
        data-testid="quote-information-status"
        level={status.level}
        text={status.text}
      />
      <p className="title-3 mb-16" data-test-quote-information-number>
        <FormattedMessage id="quote.preview.quote" values={{ quoteNumber: quote.number }} />
      </p>
      <div className={styles.price} data-test-quote-information-price>
        <h1 className={cx(styles.priceValue, 'title-1')}>
          <FormattedMoney
            amount={quote.amountDue.value}
            currency={quote.amountDue.currency}
            token="money-iso"
          />
        </h1>
        <span className={cx(styles.priceVat, 'caption')}>
          <FormattedMessage id="quote.preview.include-vat" />
        </span>
      </div>
      <div className="mb-16">
        <p className="title-4" data-test-quote-information-sent-by>
          <FormattedMessage
            id="quote.preview.sent-by"
            values={{ companyName: quote.organization.legalName }}
          />
        </p>
      </div>
      <p
        className="body-1 mb-8"
        data-test-quote-information-expiry-date
        data-testid="quote-information-expiry-date"
      >
        {hasExpired ? (
          <FormattedMessage
            id="client-portal.expired-on"
            values={{
              date: quote.expiryDate ? (
                <FormattedDate date={quote.expiryDate} locale={locale} token="compact" />
              ) : (
                ''
              ),
            }}
          />
        ) : (
          <FormattedMessage
            id="quote.preview.expiration-label"
            values={{
              date: quote.expiryDate ? (
                <FormattedDate date={quote.expiryDate} locale={locale} token="compact" />
              ) : (
                ''
              ),
            }}
          />
        )}
      </p>
    </div>
  );
}
